import {
  COLORS,
  createStoryCanvas,
  drawBadge,
  drawHeroImage,
  drawProfileHero,
  drawLogo,
  drawQrBlock,
  drawSellerLine,
  drawTextColumn,
  drawTopOverlay,
  OVERLAY_PILL_BORDER,
  OVERLAY_PILL_PRESETS,
  resolveBottomLayout,
  resolveSellerLabel,
  formatPrice,
  type OverlayPill,
  type TextBlock,
} from "./shared"
import {
  badgeForContext,
  type AuctionStoryContext,
  type BuyNowStoryContext,
  type OfferStoryContext,
  type PreorderStoryContext,
  type ScheduledStoryContext,
  type StoryCardContext,
} from "./types"

interface InfoColumn {
  primaryLabel?: string
  primaryValue: string
  primaryValueFont?: string
  secondary?: Array<{ text: string; color?: string; size?: number; weight?: "regular" | "bold"; marginTop?: number }>
  overlayRows?: OverlayPill[][]
  badgeAtTop?: boolean
}

function dateParts(iso: string | null | undefined): { weekday: string; date: string; time: string } | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return {
    weekday: d.toLocaleDateString("en-US", { weekday: "short" }),
    date: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    time: d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }),
  }
}

function relativeCountdown(iso: string | null | undefined): string | null {
  const parts = dateParts(iso)
  if (!parts || !iso) return null
  const delta = new Date(iso).getTime() - Date.now()
  if (delta <= 0) return "Ended"
  const totalMinutes = Math.floor(delta / 60000)
  const days = Math.floor(totalMinutes / (60 * 24))
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60)
  const minutes = totalMinutes % 60
  if (days >= 1) return `Ends in ${days}d ${hours}h`
  if (hours >= 1) return `Ends in ${hours}h ${minutes}m`
  return `Ends in ${minutes}m`
}

function buildBuyNowColumn(ctx: BuyNowStoryContext): InfoColumn {
  return {
    primaryValue: formatPrice(ctx.price, ctx.currency ?? "USD"),
    primaryValueFont: "bold 120px Inter, system-ui, sans-serif",
  }
}

function buildAuctionColumn(ctx: AuctionStoryContext): InfoColumn {
  const hasBids = ctx.highestBid != null && Number(ctx.highestBid) > 0
  const bidAmount = hasBids ? Number(ctx.highestBid) : Number(ctx.startPrice ?? 0)
  const label = hasBids ? "Current bid" : "Starting at"
  const statusRow: OverlayPill[] = []
  if (hasBids && ctx.bidCount != null && ctx.bidCount > 0) {
    statusRow.push({
      label: `${ctx.bidCount} BID${ctx.bidCount === 1 ? "" : "S"}`,
      ...OVERLAY_PILL_PRESETS.neutral,
    })
  }
  const countdown = relativeCountdown(ctx.endAt)
  const countdownText = countdown
    ? countdown
    : (() => {
        const parts = dateParts(ctx.endAt)
        return parts ? `Ends ${parts.weekday} ${parts.date}` : null
      })()
  const urgencyRow: OverlayPill[] = countdownText
    ? [{ label: countdownText.toUpperCase(), ...OVERLAY_PILL_PRESETS.amber }]
    : []
  return {
    primaryLabel: label,
    primaryValue: formatPrice(bidAmount, ctx.currency ?? "USD"),
    primaryValueFont: "bold 120px Inter, system-ui, sans-serif",
    overlayRows: [statusRow, urgencyRow],
    badgeAtTop: true,
  }
}

function buildPreorderColumn(ctx: PreorderStoryContext): InfoColumn {
  const statusRow: OverlayPill[] = []
  if (ctx.minOrders != null && ctx.currentOrders != null) {
    statusRow.push({
      label: `${ctx.currentOrders}/${ctx.minOrders} RESERVED`,
      ...OVERLAY_PILL_PRESETS.neutral,
    })
  }
  const delivery = dateParts(ctx.estimatedDelivery)
  if (delivery) {
    statusRow.push({
      label: `SHIPS ~${delivery.date.toUpperCase()}`,
      ...OVERLAY_PILL_PRESETS.neutral,
    })
  }
  const deadline = dateParts(ctx.deadline)
  const urgencyRow: OverlayPill[] = deadline
    ? [
        {
          label: `RESERVE BY ${deadline.weekday.toUpperCase()} ${deadline.date.toUpperCase()}`,
          ...OVERLAY_PILL_PRESETS.amber,
        },
      ]
    : []
  return {
    primaryValue: formatPrice(ctx.price, ctx.currency ?? "USD"),
    primaryValueFont: "bold 120px Inter, system-ui, sans-serif",
    overlayRows: [statusRow, urgencyRow],
    badgeAtTop: true,
  }
}

function buildOfferColumn(ctx: OfferStoryContext): InfoColumn {
  if (ctx.variant === "only") {
    return {
      primaryLabel: "Open to offers",
      primaryValue: "Make an offer",
      primaryValueFont: "bold 92px Inter, system-ui, sans-serif",
      badgeAtTop: true,
    }
  }
  return {
    primaryValue: ctx.price != null ? formatPrice(ctx.price, ctx.currency ?? "USD") : "Make an offer",
    primaryValueFont: "bold 120px Inter, system-ui, sans-serif",
    badgeAtTop: true,
  }
}

function buildScheduledColumn(ctx: ScheduledStoryContext): InfoColumn {
  const parts = dateParts(ctx.publishAt)
  const countdown = (() => {
    const delta = new Date(ctx.publishAt).getTime() - Date.now()
    if (!Number.isFinite(delta) || delta <= 0) return null
    const minutes = Math.floor(delta / 60000)
    const days = Math.floor(minutes / (60 * 24))
    const hours = Math.floor((minutes % (60 * 24)) / 60)
    const mins = minutes % 60
    if (days >= 1) return `IN ${days}D ${hours}H`
    if (hours >= 1) return `IN ${hours}H ${mins}M`
    return `IN ${mins}M`
  })()
  const statusRow: OverlayPill[] = []
  if (parts) {
    statusRow.push({
      label: `${parts.date.toUpperCase()} · ${parts.time}`,
      ...OVERLAY_PILL_PRESETS.neutral,
    })
  }
  const urgencyRow: OverlayPill[] = countdown
    ? [{ label: countdown, ...OVERLAY_PILL_PRESETS.amber }]
    : []
  const hasPrice = ctx.price != null
  return {
    primaryLabel: hasPrice ? "Drops at" : undefined,
    primaryValue: hasPrice
      ? formatPrice(ctx.price as number | string, ctx.currency ?? "USD")
      : parts
        ? parts.date
        : "Soon",
    primaryValueFont: "bold 120px Inter, system-ui, sans-serif",
    overlayRows: [statusRow, urgencyRow],
    badgeAtTop: true,
  }
}

function buildInfoColumn(ctx: StoryCardContext): InfoColumn {
  switch (ctx.kind) {
    case "auction":
      return buildAuctionColumn(ctx)
    case "preorder":
      return buildPreorderColumn(ctx)
    case "offer":
      return buildOfferColumn(ctx)
    case "scheduled":
      return buildScheduledColumn(ctx)
    case "buyNow":
    default:
      return buildBuyNowColumn(ctx)
  }
}

export async function generateStoryCardDataUrl(ctx: StoryCardContext): Promise<string> {
  const { canvas, ctx: c2d } = createStoryCanvas()

  const blurHero = ctx.kind === "scheduled"
  await drawHeroImage(c2d, ctx.imageUrl, {
    blur: blurHero,
    darken: blurHero ? 0.35 : 0,
  })
  drawLogo(c2d)

  const layout = resolveBottomLayout()
  const info = buildInfoColumn(ctx)
  const badge = badgeForContext(ctx)

  const leftX = 60
  const overlayRows: OverlayPill[][] = info.overlayRows ? info.overlayRows.map((row) => [...row]) : []
  if (info.badgeAtTop && badge) {
    const badgePill: OverlayPill = {
      label: badge.label.toUpperCase(),
      bg: badge.bg,
      fg: badge.fg,
      border: OVERLAY_PILL_BORDER,
    }
    if (overlayRows.length === 0) overlayRows.push([badgePill])
    else overlayRows[0]!.unshift(badgePill)
  }
  if (overlayRows.length > 0) {
    drawTopOverlay(c2d, overlayRows, leftX, 60)
  }

  let y = layout.contentY

  if (!info.badgeAtTop && badge) {
    const { height } = drawBadge(c2d, badge, leftX, y)
    y += height + 28
  }

  if (info.primaryLabel) {
    c2d.save()
    c2d.font = "600 34px Inter, system-ui, sans-serif"
    c2d.fillStyle = COLORS.mutedLabel
    c2d.textAlign = "left"
    c2d.fillText(info.primaryLabel.toUpperCase(), leftX, y + 34)
    c2d.restore()
    y += 48
  } else {
    y += 0
  }

  const primaryFont = info.primaryValueFont ?? "bold 120px Inter, system-ui, sans-serif"
  c2d.save()
  c2d.font = primaryFont
  c2d.fillStyle = COLORS.white
  c2d.textAlign = "left"
  const primaryMatch = primaryFont.match(/(\d+)px/)
  const primarySize = primaryMatch ? Number.parseInt(primaryMatch[1], 10) : 120
  c2d.fillText(info.primaryValue, leftX, y + primarySize)
  c2d.restore()
  y += primarySize + 32

  const titleBlocks: TextBlock[] = [
    {
      text: ctx.title,
      font: "bold 60px Inter, system-ui, sans-serif",
      color: COLORS.white,
      lineHeight: 80,
      maxLines: 3,
      marginTop: 36,
    },
  ]
  y = drawTextColumn(c2d, titleBlocks, leftX, y, layout.leftContentMaxWidth)

  if (info.secondary && info.secondary.length > 0) {
    for (const block of info.secondary) {
      const size = block.size ?? 32
      const weight = block.weight === "bold" ? "bold " : ""
      y += block.marginTop ?? 0
      c2d.save()
      c2d.font = `${weight}${size}px Inter, system-ui, sans-serif`
      c2d.fillStyle = block.color ?? COLORS.subtle
      c2d.textAlign = "left"
      c2d.fillText(block.text, leftX, y + size)
      c2d.restore()
      y += size + 14
    }
    y += 16
  }

  const sellerLabel = resolveSellerLabel(ctx.seller)
  drawSellerLine(c2d, sellerLabel, leftX, y)

  await drawQrBlock(c2d, ctx.listingUrl, sellerLabel, layout)

  return canvas.toDataURL("image/png")
}

export interface ProfileStoryCardInput {
  profileUrl: string
  username: string
  avatarUrl?: string
  bio?: string
}

export async function generateProfileStoryCardDataUrl(input: ProfileStoryCardInput): Promise<string> {
  const { canvas, ctx: c2d } = createStoryCanvas()
  const handle = input.username.replace(/^@+/, "")
  await drawProfileHero(c2d, input.avatarUrl, handle)
  drawLogo(c2d)

  const leftX = 60
  const layout = resolveBottomLayout()
  let y = layout.contentY
  const atHandle = `@${handle}`

  c2d.save()
  c2d.font = "bold 100px Inter, system-ui, sans-serif"
  c2d.fillStyle = COLORS.white
  c2d.textAlign = "left"
  c2d.fillText(atHandle, leftX, y + 100)
  c2d.restore()
  y += 100 + 32

  const bio = input.bio?.trim()
  if (bio) {
    y = drawTextColumn(
      c2d,
      [
        {
          text: bio,
          font: "600 40px Inter, system-ui, sans-serif",
          color: COLORS.subtle,
          lineHeight: 52,
          maxLines: 3,
          marginTop: 36,
        },
      ],
      leftX,
      y,
      layout.leftContentMaxWidth,
    )
    y += 24
  }

  await drawQrBlock(c2d, input.profileUrl, atHandle, layout, { scanLine: "Scan to view profile" })
  return canvas.toDataURL("image/png")
}

export function downloadStoryCard(dataUrl: string, filename: string) {
  const link = document.createElement("a")
  link.download = filename
  link.href = dataUrl
  link.click()
}

export * from "./types"
